import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBrand } from '@/config/brands';

export type PushTopic = 'compliance' | 'pricing' | 'legislation' | 'general';

interface PushSubscriptionState {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  subscribe: (stateCode?: string, topics?: PushTopic[]) => Promise<void>;
  unsubscribe: () => Promise<void>;
  updatePreferences: (stateCode?: string, topics?: PushTopic[]) => Promise<void>;
}

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications(): PushSubscriptionState {
  const brand = useBrand();
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    !!VAPID_PUBLIC_KEY;

  // Check existing subscription on mount
  useEffect(() => {
    if (!isSupported) return;
    navigator.serviceWorker.ready.then(registration => {
      registration.pushManager.getSubscription().then(sub => {
        setIsSubscribed(!!sub);
      });
    });
  }, [isSupported]);

  const subscribe = useCallback(
    async (
      stateCode?: string,
      topics: PushTopic[] = ['compliance', 'pricing', 'legislation', 'general']
    ) => {
      if (!isSupported) {
        setError('Push notifications are not supported in this browser');
        return;
      }
      setIsLoading(true);
      setError(null);

      try {
        // 1. Request permission
        const perm = await Notification.requestPermission();
        setPermission(perm);
        if (perm !== 'granted') {
          setError('Notification permission denied');
          return;
        }

        // 2. Get SW registration
        const registration = await navigator.serviceWorker.ready;

        // 3. Subscribe to push
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });

        const json = subscription.toJSON();

        // 4. Store via SECURITY DEFINER RPC (2026-08-02).
        //    Was a direct .upsert() on push_subscriptions, which failed for EVERY
        //    visitor with 42501 "permission denied": anon holds INSERT and UPDATE
        //    but not SELECT, and INSERT ... ON CONFLICT DO UPDATE has to read the
        //    conflicting row. Granting anon SELECT was not an option — endpoint +
        //    p256dh + auth together are a credential for pushing to that browser,
        //    so a read grant would publish every subscriber's push credentials.
        //    register_push_subscription() runs as its owner, writes one row and
        //    returns nothing, so anon needs no table privileges at all.
        const { error: dbError } = await supabase.rpc(
          'register_push_subscription' as any,
          {
            p_endpoint: json.endpoint,
            p_p256dh: json.keys!.p256dh,
            p_auth: json.keys!.auth,
            p_state_code: stateCode || null,
            p_topics: topics,
            p_brand: brand.key,
            p_user_agent: navigator.userAgent,
          } as any
        );

        if (dbError) throw dbError;
        setIsSubscribed(true);
      } catch (err: any) {
        setError(err.message || 'Failed to subscribe');
        console.error('Push subscription error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [isSupported, brand.key]
  );

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();

        // Mark inactive via RPC — same reason as register: UPDATE ... WHERE
        // endpoint = $1 needs SELECT on endpoint, which anon does not have.
        await supabase.rpc('update_push_subscription' as any, {
          p_endpoint: endpoint,
          p_is_active: false,
        } as any);
      }
      setIsSubscribed(false);
    } catch (err: any) {
      setError(err.message || 'Failed to unsubscribe');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updatePreferences = useCallback(
    async (stateCode?: string, topics?: PushTopic[]) => {
      setIsLoading(true);
      setError(null);
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) return;

        // Via RPC for the same permission reason. Undefined args are COALESCEd
        // server-side, so changing only topics leaves state_code untouched.
        await supabase.rpc('update_push_subscription' as any, {
          p_endpoint: subscription.endpoint,
          p_state_code: stateCode !== undefined ? (stateCode || null) : null,
          p_topics: topics ?? null,
        } as any);
      } catch (err: any) {
        setError(err.message || 'Failed to update preferences');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    updatePreferences,
  };
}
